import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {css} from '@emotion/css';
import {StandardTypography} from 'patient-ping-remedy/packages/typography';
import {SmallTypography} from 'patient-ping-remedy/packages/typography/lib/';
import Spinner from 'patient-ping-remedy/packages/spinner';
import Modal from 'patient-ping-remedy/packages/modal';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import Button, {StyleType} from 'patient-ping-remedy/packages/button';
import {ToastType} from 'patient-ping-remedy/packages/toast';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import {JobStatus} from '@/api/dto/client-profile';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAlertStore} from '@/store/alert_store';
import {createFormFieldConfig, useForm} from '@/hooks/useForm';
import SortableTable, {SORT_ORDER, TableData} from '@common/SortableTable';
import {nonGroupedValues} from '@common/FilterComponent';
import BulkUserUploadForm from '@/components/pages/tools/bulk_user_upload/BulkUserUploadForm';
import Validate from '@/helpers/validate';
import FileHelpers from '@/helpers/file_helpers';
import DisplayHelpers from '@/helpers/display_helpers';
import {AxiosError} from "axios";
import Helpers from "@/helpers/helpers";

const statusDisplayValues: Record<JobStatus, { color: colors, icon: ICONS }> = {
  [JobStatus.Complete]: {
    color: colors.green5,
    icon: ICONS.check,
  },
  [JobStatus.InProgress]: {
    color: colors.blue4,
    icon: ICONS.spinner,
  },
  [JobStatus.Retrying]: {
    color: colors.yellow5,
    icon: ICONS.redo,
  },
  [JobStatus.Failed]: {
    color: colors.red5,
    icon: ICONS['exclamation-triangle'],
  },
};

const statusFilterOptions: nonGroupedValues[] = Object.values(JobStatus).map((jobStatus) => ({
  id: jobStatus,
  value: jobStatus,
  label: DisplayHelpers.humanize(jobStatus),
}));

const tableHeaders = [
  "Start date",
  "Finish date",
  "Status",
  "Upload",
  "Result",
  "Total Record Count",
  "New Users Created",
  "Existing Users Updated",
  "Errors"
];

export default function BulkUserUploadTool({pollingInterval = 30000}: {pollingInterval?: number}) {
  const { carecoApi } = useContext(CarecoApiContext);
  const { addAlert } = useAlertStore();
  const queryClient = useQueryClient();

  const [inProgress, setInProgress] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { data: bulkUserUploads, isLoading, error } = useQuery({
    queryKey: ['bulkUserUploads'],
    queryFn: () => carecoApi?.getBulkUserUploadTool(),
    enabled: !!carecoApi,
    refetchInterval: pollingInterval,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: async (file: File) =>
      carecoApi?.postBulkUserUploadTool(file),
    onSuccess() {
      addAlert({
        type: ToastType.SUCCESS,
        content: 'File uploaded successfully. Bulk user upload will now start in the background.',
      });
      void queryClient.invalidateQueries({ queryKey: ['bulkUserUploads'] });
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      setIsUploadModalOpen(false);
    },
    onError(error: AxiosError) {
      Helpers.alertOnError('Failed to upload file. Bulk user upload will not be started.', error);
    },
  });

  const { form, isValid, setInitialForm, resetForm, handleChange, handleSubmit } = useForm(() => {
    mutate(form.file.value[0]);
  });

  useEffect(() => {
    if (isUploadModalOpen) {
      setInitialForm({
        ...createFormFieldConfig(
          'file',
          [],
          { valid: false, message: '' },
          [Validate.validateFile()],
          true,
        ),
      });
    }
  }, [isUploadModalOpen]);

  useEffect(() => {
    const inProgressNew = (bulkUserUploads ?? []).some((bulkUserUpload) =>
      bulkUserUpload.status === JobStatus.InProgress);

    if (inProgress && !inProgressNew) {
      addAlert({
        type: ToastType.SUCCESS,
        content: 'File processed successfully. Verify if there are any error messages in results file.',
      });
    }

    setInProgress(inProgressNew);
  }, [bulkUserUploads]);

  const tableData = useMemo(() => (bulkUserUploads ?? []).map((bulkUserUpload): TableData => {
    const uploadFilename = bulkUserUpload.uploadS3Key.split('/').pop() ?? '';
    const resultFilename = bulkUserUpload.resultS3Key?.split('/').pop() ?? '';

    return {
      startDate: {
        tableHeader: 'Start date',
        key: 'startDate',
        type: 'string',
        value: bulkUserUpload.createdAt,
        displayValue: DisplayHelpers.formatDateTime(bulkUserUpload.createdAt, true),
        columnWidth: '15%',
      },
      finishDate: {
        tableHeader: 'Finish date',
        key: 'finishDate',
        type: 'string',
        value: bulkUserUpload.finishedAt ?? '',
        displayValue: bulkUserUpload.finishedAt ? DisplayHelpers.formatDateTime(bulkUserUpload.finishedAt, true) : '',
        columnWidth: '15%',
      },
      status: {
        tableHeader: 'Status',
        key: 'status',
        type: 'string',
        value: DisplayHelpers.humanize(bulkUserUpload.status),
        displayValue: (
          <StandardTypography color={statusDisplayValues[bulkUserUpload.status].color}>
            <Icon
              weight="far"
              iconClass={statusDisplayValues[bulkUserUpload.status].icon}
              spin={bulkUserUpload.status === JobStatus.InProgress}
              className={css({ marginRight: sizes.xxSmall })}
            />
            {DisplayHelpers.humanize(bulkUserUpload.status)}
          </StandardTypography>
        ),
        columnWidth: '10%',
      },
      upload: {
        tableHeader: 'Upload',
        key: 'upload',
        type: 'string',
        value: uploadFilename,
        displayValue: (
          <Button
            styleType={StyleType.TERTIARY}
            className={css({ marginLeft: '-1.4rem' })}
            onClick={() => handleDownload(bulkUserUpload.id, uploadFilename)}
          >
            {uploadFilename}
          </Button>
        ),
        columnWidth: '20%',
      },
      result: {
        tableHeader: 'Result',
        key: 'result',
        type: 'string',
        value: resultFilename,
        displayValue: (
          <Button
            styleType={StyleType.TERTIARY}
            className={css({ marginLeft: '-1.4rem' })}
            onClick={() => handleDownload(bulkUserUpload.id, resultFilename, true)}
          >
            {resultFilename}
          </Button>
        ),
        columnWidth: '20%',
      },
      totalRecords: {
        tableHeader: 'Total Record Count',
        key: 'totalRecords',
        type: 'number',
        value: bulkUserUpload.totalRecords ?? '',
        displayValue: bulkUserUpload.totalRecords?.toString() ?? '',
        columnWidth: '5%',
      },
      newUsersCreated: {
        tableHeader: 'New Users Created',
        key: 'newUsersCreated',
        type: 'number',
        value: bulkUserUpload.newUsersCreated ?? '',
        displayValue: bulkUserUpload.newUsersCreated?.toString() ?? '',
        columnWidth: '5%',
      },
      existingUsersUpdated: {
        tableHeader: 'Existing Users Updated',
        key: 'existingUsersUpdated',
        type: 'number',
        value: bulkUserUpload.existingUsersUpdated ?? '',
        displayValue: bulkUserUpload.existingUsersUpdated?.toString() ?? '',
        columnWidth: '5%',
      },
      recordsWithErrors: {
        tableHeader: 'Errors',
        key: 'recordsWithErrors',
        type: 'number',
        value: bulkUserUpload.recordsWithErrors ?? '',
        displayValue: bulkUserUpload.recordsWithErrors?.toString() ?? '',
        columnWidth: '5%',
      },
    };
  }), [bulkUserUploads]);

  const handleDownload = async (bulkUserUploadId: number, filename: string, results: boolean = false) => {
    const response = await carecoApi?.getBulkUserUploadToolFile(bulkUserUploadId, results);
    if (response) {
      const arrayBuffer = FileHelpers.base64ToArrayBuffer(response);
      const blob = new Blob([arrayBuffer], {type: 'text/csv'});
      FileHelpers.downloadFile(URL.createObjectURL(blob), filename);
    }
  };

  const handleOpen = () => {
    setIsUploadModalOpen(true);
  };

  const handleClose = () => {
    setIsUploadModalOpen(false);
    resetForm();
  };

  return (
    <>
      <SortableTable
        id="bulk-user-upload-table"
        loading={isLoading}
        loadError={error}
        data={tableData}
        header={tableHeaders}
        sortableColumns={tableData[0] ? [
          tableData[0]['startDate'],
          tableData[0]['finishDate'],
        ] : []}
        searchColumns={['upload', 'result']}
        searchPlaceholder="Search by upload or result file names"
        filterOptions={[
          {
            type: 'multiselect-search',
            label: 'Status',
            dataType: 'string',
            key: 'status',
            options: statusFilterOptions,
          },
        ]}
        tableOverrides={{
          initialSort: tableData[0] ? {
            tableColumn: tableData[0].startDate,
            sortOrder: SORT_ORDER.DESC
          } : null
        }}
      >
        {inProgress && (
          <>
            <Spinner iconSize="2x" />
            <SmallTypography color={colors.charcoal}>
              Upload - this may take some time. The process will continue if you leave this page.
            </SmallTypography>
          </>
        )}
        <Button disabled={isLoading || inProgress || isPending} onClick={handleOpen}>
          <Icon iconClass={ICONS.plus} style={{ marginRight: sizes.xSmall }} />
          Upload New File
        </Button>
      </SortableTable>

      <Modal
        headerText="Upload New File"
        isOpen={isUploadModalOpen}
        hideModal={handleClose}
        buttons={[
          <Button key="upload" disabled={!isValid() || isPending} onClick={handleSubmit}>Upload</Button>,
          <Button key="cancel" styleType={StyleType.TERTIARY} onClick={handleClose}>Cancel</Button>
        ]}
        className={css({ width: '70rem' })}
      >
        <BulkUserUploadForm form={form} handleChange={handleChange} />
      </Modal>
    </>
  );
}
